import React, { useState } from "react";
import { Link } from "react-router-dom";

const initialFormState = {
  name: "",
  email: "",
  phone: "",
  message: "",
  consent: false,
};

function ContactFormComponent({ endpoint }) {
  const [formData, setFormData] = useState(initialFormState);
  const [feedbackMessage, setFeedbackMessage] = useState({
    success: "",
    error: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((previousState) => ({
      ...previousState,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedData = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      message: formData.message.trim(),
    };

    if (!trimmedData.name || !trimmedData.email || !trimmedData.message) {
      setFeedbackMessage({
        success: "",
        error: "Merci de renseigner votre nom, votre email et votre message.",
      });
      return;
    }

    if (trimmedData.message.length < 10) {
      setFeedbackMessage({
        success: "",
        error: "Votre message doit contenir au moins 10 caractères.",
      });
      return;
    }

    if (!trimmedData.consent) {
      setFeedbackMessage({
        success: "",
        error: "Vous devez accepter le traitement de vos données personnelles.",
      });
      return;
    }

    setIsSubmitting(true);
    setFeedbackMessage({ success: "", error: "" });

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(trimmedData),
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          payload?.message ||
            "Une erreur est survenue lors de l’envoi du message.",
        );
      }

      setFeedbackMessage({
        success:
          payload?.message ||
          "Merci pour votre message. Je vous répondrai dans les meilleurs délais.",
        error: "",
      });
      setFormData(initialFormState);
    } catch (error) {
      setFeedbackMessage({
        success: "",
        error:
          error.message ||
          "Erreur lors de l’envoi du message. Veuillez réessayer.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="section-card" noValidate>
      {feedbackMessage.success ? (
        <div
          className="mb-6 rounded-soft border border-green-200 bg-green-50 px-4 py-3 text-green-800"
          role="status"
          aria-live="polite"
        >
          {feedbackMessage.success}
        </div>
      ) : null}

      {feedbackMessage.error ? (
        <div
          className="mb-6 rounded-soft border border-red-200 bg-red-50 px-4 py-3 text-red-700"
          role="alert"
          aria-live="assertive"
        >
          {feedbackMessage.error}
        </div>
      ) : null}

      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-2 block text-sm font-semibold text-ink">
            Nom
          </label>
          <input
            className="input-field"
            id="name"
            name="name"
            type="text"
            placeholder="Votre nom"
            value={formData.name}
            onChange={handleInputChange}
            autoComplete="name"
            maxLength={80}
            required
          />
        </div>

        <div>
          <label htmlFor="email" className="mb-2 block text-sm font-semibold text-ink">
            Email
          </label>
          <input
            className="input-field"
            id="email"
            name="email"
            type="email"
            placeholder="votre@email.com"
            value={formData.email}
            onChange={handleInputChange}
            autoComplete="email"
            maxLength={120}
            required
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="phone" className="mb-2 block text-sm font-semibold text-ink">
          Téléphone
        </label>
        <input
          className="input-field"
          id="phone"
          name="phone"
          type="tel"
          placeholder="Optionnel"
          value={formData.phone}
          onChange={handleInputChange}
          autoComplete="tel"
          maxLength={30}
        />
      </div>

      <div className="mt-5">
        <label htmlFor="message" className="mb-2 block text-sm font-semibold text-ink">
          Message
        </label>
        <textarea
          className="input-field min-h-[180px] resize-y"
          id="message"
          name="message"
          placeholder="Votre message"
          rows="7"
          value={formData.message}
          onChange={handleInputChange}
          maxLength={2000}
          required
        />
      </div>

      <div className="mt-6 rounded-soft border border-line bg-surface px-4 py-4">
        <div className="flex items-start gap-3">
          <input
            className="mt-1 h-4 w-4 rounded border-line text-brandDeep focus:ring-brand"
            id="consent"
            type="checkbox"
            name="consent"
            checked={formData.consent}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="consent" className="text-sm leading-6 text-muted">
            J’accepte le traitement de mes données personnelles conformément à la{" "}
            <Link
              to="/politique-de-confidentialite"
              className="content-link focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              politique de confidentialité
            </Link>{" "}
            afin de recevoir une réponse à mon message.
          </label>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3">
        <button
          type="submit"
          disabled={isSubmitting}
          className="button-primary w-full"
          style={{ display: "flex", width: "100%" }}
        >
          {isSubmitting ? "Envoi en cours..." : "Envoyer ma demande"}
        </button>
        <p className="text-center text-sm text-muted">
          Réponse envoyée à l’adresse indiquée dans votre message.
        </p>
      </div>
    </form>
  );
}

export default ContactFormComponent;
